import { useEffect } from 'react';
import { FormikProps, FormikProvider } from 'formik';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes, ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import addPathPrefix from 'utils/addPathPrefix';
import NewFormCard, { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import DeclarationInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import PlacesCard from '../cards/places-card/PlacesCard';
import DeclarationDatepicker from 'components/ui/composed/declarations/formDatepicker/DeclarationDatepicker';
import onDateTimeChange from './onDateTimeChange';
import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import PartiesCard from '../cards/parties-card/PartiesCard';
import CountriesOfRoutingField from './cards/CountriesOfRoutingField';
import {
    AdditionalInformationCard,
    AdditionalSupplyChainActorCard,
    SupportingDocumentsCard,
    TransportEquipmentCard,
} from './cards/SharedCards';
import useICS2Codelists from 'hooks/useICS2Codelists';
import useHideEntities from 'hooks/useHideEntities';

interface Props {
    formik: FormikProps<any>;
    customerId: string | undefined;
    viewOnly?: boolean;
}

const MASTER = 'consignmentMasterLevel';
const TRANSPORT = 'activeBorderTransportMeans';

const ROUTING_PATH = `${TRANSPORT}.countriesOfRoutingOfMeansOfTransport`;

const ICS2ENSMasterForm = ({ formik, customerId, viewOnly }: Props) => {
    const { preloadICS2Codelists } = useICS2Codelists();
    const { isFormType } = useHideEntities();

    const isF50 = isFormType([ICS2MessageTypes.F50]);

    useEffect(() => {
        preloadICS2Codelists([
            ICS2CodelistsMap.CL013,
            ICS2CodelistsMap.CL718,
            ICS2CodelistsMap.CL710,
            ICS2CodelistsMap.CL709,
            ICS2CodelistsMap.CL711,
            ICS2CodelistsMap.CL704,
            ICS2CodelistsMap.CL707,
            ICS2CodelistsMap.CL094,
            ICS2CodelistsMap.CL141,
        ]);
    }, [preloadICS2Codelists]);

    return (
        <FormikProvider value={formik}>
            <NewFormCard title="Master Details">
                <FormCardContainer>
                    <DeclarationInput
                        refNumber="12 08 001 000"
                        label="Reference Number/UCR"
                        {...getFormikProps(addPathPrefix(MASTER, 'referenceNumberUCR.referenceNumberUCR'), formik)}
                        condensed
                    />
                    <DeclarationSelect
                        hideCodelistMenu
                        placeholder="Select Specific Circumstance Indicator"
                        refNumber="11 04 001 000"
                        required
                        condensed
                        label="Specific Circumstance Indicator"
                        {...getFormikProps('specificCircumstanceIndicator', formik)}
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL742,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <DeclarationDatepicker
                        showTime
                        required
                        condensed
                        showDateFormatLabel={false}
                        label="Document Issue Date"
                        refNumber="11 03 001 000"
                        {...getFormikProps('documentIssueDate.dateTime', formik)}
                        onChange={onDateTimeChange(formik, 'documentIssueDate')}
                    />
                    <DeclarationNumberInput
                        refNumber="18 03 001 000"
                        label="Total Gross Mass"
                        maxLength={16}
                        precision={6}
                        {...getFormikProps(addPathPrefix(MASTER, 'totalGrossMass'), formik)}
                        condensed
                        required
                        hidden={isF50}
                    />
                </FormCardContainer>
            </NewFormCard>

            <NewFormCard hidden={isF50} title="Split Consignment">
                <FormCardContainer>
                    <DeclarationSelect
                        hideCodelistMenu
                        placeholder="Select Split Consignment Indicator"
                        refNumber="11 06 001 000"
                        required
                        condensed
                        label="Split Consignment Indicator"
                        {...getFormikProps('splitConsignment.splitConsignmentIndicator', formik)}
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL027,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <DeclarationInput
                        refNumber="11 06 002 000"
                        label="Previous MRN"
                        {...getFormikProps('splitConsignment.previousMRN', formik)}
                        condensed
                    />
                </FormCardContainer>
            </NewFormCard>

            <PartiesCard
                viewOnly={viewOnly}
                formik={formik}
                parties={[
                    {
                        tabName: 'Consignor',
                        label: 'Consignor',
                        required: true,
                        referenceNumber: '13 02 000 000',
                        path: addPathPrefix(MASTER, 'consignor'),
                        partyReferences: {
                            name: '13 02 016 000',
                            identificationNumber: '13 02 017 000',
                            typeOfPerson: '13 02 019 000',
                            streetAndNumber: '13 02 018 023',
                            postCode: '13 02 018 021',
                            city: '13 02 018 022',
                            country: '13 02 018 020',
                        },
                    },
                    {
                        tabName: 'Consignee',
                        label: 'Consignee',
                        required: true,
                        referenceNumber: '13 03 000 000',
                        path: addPathPrefix(MASTER, 'consignee'),
                        partyReferences: {
                            name: '13 03 016 000',
                            identificationNumber: '13 03 017 000',
                            typeOfPerson: '13 03 019 000',
                            streetAndNumber: '13 03 018 019',
                            postCode: '13 03 018 021',
                            city: '13 03 018 022',
                            country: '13 03 018 020',
                        },
                    },
                    {
                        tabName: 'Declarant',
                        label: 'Declarant',
                        required: true,
                        eoriRequired: true,
                        communicationRequired: true,
                        referenceNumber: '13 05 000 000',
                        path: 'declarant',
                        partyReferences: {
                            name: '13 05 016 000',
                            identificationNumber: '13 05 017 000',
                            streetAndNumber: '13 05 018 023',
                            postCode: '13 05 018 021',
                            city: '13 05 018 022',
                            country: '13 05 018 020',
                        },
                    },
                    {
                        tabName: 'Representative',
                        label: 'Representative',
                        required: false,
                        eoriRequired: true,
                        communicationRequired: true,
                        hasStatus: true,
                        referenceNumber: '13 06 000 000',
                        path: 'representative',
                        partyReferences: {
                            name: '13 06 016 000',
                            identificationNumber: '13 06 017 000',
                            streetAndNumber: '13 06 018 023',
                            postCode: '13 06 018 021',
                            city: '13 06 018 022',
                            country: '13 06 018 020',
                        },
                    },
                    {
                        tabName: 'Carrier',
                        label: 'Carrier',
                        required: true,
                        eoriRequired: true,
                        referenceNumber: '13 12 000 000',
                        path: addPathPrefix(MASTER, 'carrier'),
                        partyReferences: {
                            name: '13 12 016 000',
                            identificationNumber: '13 12 017 000',
                            streetAndNumber: '13 12 018 023',
                            postCode: '13 12 018 021',
                            city: '13 12 018 022',
                            country: '13 12 018 020',
                        },
                    },
                    // The master level of F50 carries no notify party.
                    ...(isF50
                        ? []
                        : [
                              {
                                  tabName: 'Notify Party',
                                  label: 'Notify Party',
                                  // Optional (`O`, 0..1) on this level, but it names exactly one
                                  // means of contact once it is there.
                                  required: false,
                                  singleCommunication: true,
                                  referenceNumber: '13 13 000 000',
                                  path: addPathPrefix(MASTER, 'notifyParty'),
                                  partyReferences: {
                                      name: '13 13 016 000',
                                      identificationNumber: '13 13 017 000',
                                      typeOfPerson: '13 13 019 000',
                                      streetAndNumber: '13 13 018 023',
                                      postCode: '13 13 018 021',
                                      city: '13 13 018 022',
                                      country: '13 13 018 020',
                                  },
                              },
                          ]),
                ]}
            />

            <NewFormCard title="Transport Details">
                <FormCardContainer>
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Mode of Transport at the Border"
                        required
                        refNumber={'19 03 001 000'}
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'modeOfTransport'), formik)}
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL018,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Active Border Transport Means Type"
                        required
                        refNumber={'19 08 067 000'}
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'typeOfMeansOfTransport'), formik)}
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL751,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Active Border Transport Means ID Type"
                        required
                        refNumber={'19 08 061 000'}
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'typeOfIdentification'), formik)}
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL750,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <DeclarationInput
                        required
                        refNumber="19 08 017 000"
                        label="Active Border Transport Means ID"
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'identificationNumber'), formik)}
                        condensed
                    />

                    <DeclarationSelect
                        hideCodelistMenu
                        label="Active Border Transport Means Nationality"
                        required
                        refNumber={'19 08 062 000'}
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'nationality'), formik)}
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL718,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Transport Method of Payment"
                        required
                        refNumber={'14 02 038 000'}
                        {...getFormikProps(addPathPrefix(MASTER, 'transportCharges.methodOfPayment'), formik)}
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL116,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    {/* Optional (`O`, 0..1) on both message types; R3053 ties which departure is
                        expected to the mode of transport, which the customs system checks. */}
                    <DeclarationDatepicker
                        showTime
                        condensed
                        showDateFormatLabel={false}
                        label="Estimated date and time of departure"
                        refNumber="15 01 001 000"
                        {...getFormikProps(
                            addPathPrefix(TRANSPORT, 'estimatedDateAndTimeOfDeparture.dateTime'),
                            formik
                        )}
                        onChange={onDateTimeChange(
                            formik,
                            addPathPrefix(TRANSPORT, 'estimatedDateAndTimeOfDeparture'),
                            {
                                optional: true,
                            }
                        )}
                    />
                    <DeclarationDatepicker
                        showTime
                        condensed
                        showDateFormatLabel={false}
                        label="Actual date and time of departure"
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'actualDateAndTimeOfDeparture.dateTime'), formik)}
                        onChange={onDateTimeChange(formik, addPathPrefix(TRANSPORT, 'actualDateAndTimeOfDeparture'), {
                            optional: true,
                        })}
                    />
                    <DeclarationDatepicker
                        showTime
                        required
                        condensed
                        showDateFormatLabel={false}
                        label="Estimated date and time of arrival"
                        refNumber="15 03 001 000"
                        {...getFormikProps(addPathPrefix(TRANSPORT, 'estimatedDateAndTimeOfArrival.dateTime'), formik)}
                        onChange={onDateTimeChange(formik, addPathPrefix(TRANSPORT, 'estimatedDateAndTimeOfArrival'))}
                    />
                </FormCardContainer>
            </NewFormCard>

            <PlacesCard
                path={MASTER}
                formik={formik}
                getFieldHelpers={formik.getFieldHelpers}
                getFieldProps={formik.getFieldProps}
            />

            <NewFormCard title="Transport Document">
                <FormCardContainer>
                    <DeclarationInput
                        required
                        condensed
                        refNumber="12 05 001 000"
                        label="Document Number"
                        {...getFormikProps(
                            addPathPrefix(MASTER, 'transportDocumentMasterLevel.documentNumber'),
                            formik
                        )}
                    />
                    <DeclarationSelect
                        required
                        condensed
                        hideCodelistMenu
                        label="Type"
                        refNumber="12 05 002 000"
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL754,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps(addPathPrefix(MASTER, 'transportDocumentMasterLevel.type'), formik)}
                    />
                </FormCardContainer>
            </NewFormCard>

            <SupportingDocumentsCard formik={formik} path={MASTER} viewOnly={viewOnly} />

            <NewFormCard title="Customs Office Information">
                <FormCardContainer>
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Customs Office of First Entry Reference"
                        required
                        condensed
                        refNumber="17 07 001 000"
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL141,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps('customsOfficeOfFirstEntry.referenceNumber', formik)}
                    />
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Re-entry Indicator"
                        required
                        refNumber={'11 05 001 000'}
                        {...getFormikProps('reEntryIndicator', formik)}
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL027,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                    />
                    <CountriesOfRoutingField
                        formik={formik}
                        path={ROUTING_PATH}
                        label="Country of Routing of Means of Transport"
                        refNumber="16 11 020 000"
                        codelistName={ICS2CodelistsMap.CL718}
                    />
                </FormCardContainer>
            </NewFormCard>

            <NewFormCard hidden={isF50} title="Container">
                <FormCardContainer>
                    <DeclarationSelect
                        hideCodelistMenu
                        label="Container Indicator"
                        refNumber={'19 01 001 000'}
                        codeValidation={[]}
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL708,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        required
                        condensed
                        {...getFormikProps(addPathPrefix(MASTER, 'containerIndicator'), formik)}
                    />
                </FormCardContainer>
            </NewFormCard>

            <TransportEquipmentCard formik={formik} path={MASTER} hidden={isF50} />

            <AdditionalSupplyChainActorCard formik={formik} path={MASTER} viewOnly={viewOnly} />

            <AdditionalInformationCard formik={formik} path={MASTER} hidden={isF50} viewOnly={viewOnly} />
        </FormikProvider>
    );
};

export default ICS2ENSMasterForm;
